/**
 * Calendar Report API Configuration
 * 
 * This file contains the API endpoint mappings for different portals and roles
 * for fetching and saving report images (Therapy, Screening, Prescription, Assessment).
 * 
 * IMPORTANT: 
 * - Mentor Portal, CA Portal, and User Portal CANNOT access Control Center APIs (/api/secure/cc/*)
 * - Support Portal has multiple roles with different API endpoints
 * 
 * TODO: Update this configuration based on the API definitions document (.md file)
 */

import { getAllRolesInBoolean, getUserRoles } from 'src/mentor/layout/utils'

/**
 * Portal types
 */
export const PORTAL_TYPES = {
  MENTOR: 'mentor',
  CLINIC_ASSISTANT: 'clinic-assistant',
  SUPPORT: 'support',
  CLINIC: 'clinic',
  CONSUMER: 'consumer',
  BUSINESS: 'business',
}

/**
 * Report types
 * These match the EMRBasedReportTypes enum values
 */
export const REPORT_TYPES = {
  THERAPY: 'therapy-report',
  SCREENING: 'screening-report',
  PRESCRIPTION: 'prescription',
  ASSESSMENT: 'assessment-report',
  INVESTIGATION: 'investigation-report',
}

/**
 * API operation types
 */
export const API_OPERATIONS = {
  GET: 'get',
  SAVE: 'save',
  UPLOAD: 'upload',
}

/**
 * Base API configuration structure
 * Structure: portalType -> reportType -> operation -> apiEndpoint
 * 
 * All roles use the same pattern:
 * - GET: /api/secure/{basePath}/emr-reports/{reportType}/images/get-by-eventId
 * - POST: /api/secure/{basePath}/emr-reports/{reportType}/images/save
 * - Upload: /api/secure/{basePath}/emr-reports/file/upload
 */
const API_CONFIG = {
  [PORTAL_TYPES.MENTOR]: {
    [REPORT_TYPES.THERAPY]: {
      [API_OPERATIONS.GET]: '/api/secure/mentor/emr-reports/therapy-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/mentor/emr-reports/therapy-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/mentor/emr-reports/file/upload',
    },
    [REPORT_TYPES.SCREENING]: {
      [API_OPERATIONS.GET]: '/api/secure/mentor/emr-reports/screening-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/mentor/emr-reports/screening-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/mentor/emr-reports/file/upload',
    },
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: '/api/secure/mentor/emr-reports/prescription/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/mentor/emr-reports/prescription/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/mentor/emr-reports/file/upload',
    },
    [REPORT_TYPES.ASSESSMENT]: {
      [API_OPERATIONS.GET]: '/api/secure/mentor/emr-reports/assessment-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/mentor/emr-reports/assessment-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/mentor/emr-reports/file/upload',
    },
    [REPORT_TYPES.INVESTIGATION]: {
      [API_OPERATIONS.GET]: '/api/secure/mentor/emr-reports/investigation-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/mentor/emr-reports/investigation-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/mentor/emr-reports/file/upload',
    },
  },
  [PORTAL_TYPES.CLINIC_ASSISTANT]: {
    [REPORT_TYPES.THERAPY]: {
      [API_OPERATIONS.GET]: '/api/secure/clinic-assistant/emr-reports/therapy-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/clinic-assistant/emr-reports/therapy-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/clinic-assistant/emr-reports/file/upload',
    },
    [REPORT_TYPES.SCREENING]: {
      [API_OPERATIONS.GET]: '/api/secure/clinic-assistant/emr-reports/screening-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/clinic-assistant/emr-reports/screening-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/clinic-assistant/emr-reports/file/upload',
    },
    [REPORT_TYPES.PRESCRIPTION]: {
      // Legacy /prescription/... path still works, but using new pattern
      [API_OPERATIONS.GET]: '/api/secure/clinic-assistant/emr-reports/prescription/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/clinic-assistant/emr-reports/prescription/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/clinic-assistant/emr-reports/file/upload',
    },
    [REPORT_TYPES.ASSESSMENT]: {
      [API_OPERATIONS.GET]: '/api/secure/clinic-assistant/emr-reports/assessment-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/clinic-assistant/emr-reports/assessment-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/clinic-assistant/emr-reports/file/upload',
    },
    [REPORT_TYPES.INVESTIGATION]: {
      [API_OPERATIONS.GET]: '/api/secure/clinic-assistant/emr-reports/investigation-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/clinic-assistant/emr-reports/investigation-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/clinic-assistant/emr-reports/file/upload',
    },
  },
  [PORTAL_TYPES.SUPPORT]: {
    // Support portal uses /api/secure/cc/support/emr-reports/{reportType}/images/...
    // Prescription is CSA/CSM-only and routed separately under /api/secure/cc/support/csm/...
    [REPORT_TYPES.THERAPY]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/emr-reports/therapy-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/emr-reports/therapy-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/emr-reports/file/upload',
    },
    [REPORT_TYPES.SCREENING]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/emr-reports/screening-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/emr-reports/screening-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/emr-reports/file/upload',
    },
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.ASSESSMENT]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/emr-reports/assessment-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/emr-reports/assessment-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/emr-reports/file/upload',
    },
    [REPORT_TYPES.INVESTIGATION]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/emr-reports/investigation-report/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/emr-reports/investigation-report/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/emr-reports/file/upload',
    },
  },
  [PORTAL_TYPES.CLINIC]: {
    // Clinic portal APIs not defined in the documentation
    // These may not be available or may use different patterns
    [REPORT_TYPES.THERAPY]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.SCREENING]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.ASSESSMENT]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.INVESTIGATION]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
  },
  [PORTAL_TYPES.CONSUMER]: {
    // Consumer/User portal typically doesn't have access to these APIs
    [REPORT_TYPES.THERAPY]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.SCREENING]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.ASSESSMENT]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
    [REPORT_TYPES.INVESTIGATION]: {
      [API_OPERATIONS.GET]: null,
      [API_OPERATIONS.SAVE]: null,
      [API_OPERATIONS.UPLOAD]: null,
    },
  },
}

/**
 * Support role-specific API overrides
 * 
 * Note: Support portal uses /api/secure/cc/support/emr-reports/... for
 * non-prescription report images. CSA/CSM-only prescription flows use
 * /api/secure/cc/support/csm/emr-reports/...
 * 
 * Role access control (enforced server-side):
 * - therapy-report: ROLE_THERAPY_SUP_L3, ROLE_THERAPIST_SUPERVISOR, ROLE_THERAPY_SUPERVISOR
 * - screening-report: ROLE_THERAPY_SUP_L3, ROLE_SCREENER_SUPERVISOR
 * - assessment-report: ROLE_THERAPY_SUP_L3, ROLE_THERAPY_SUPERVISOR
 * - investigation-report: ROLE_THERAPY_SUP_L3, ROLE_THERAPY_SUPERVISOR, ROLE_SCREENER_SUPERVISOR
 */
const SUPPORT_ROLE_API_OVERRIDES = {
  // Clinic Success Manager/Admin use a dedicated /csm path for prescription only.
  'ROLE_CLINIC_SUCCESS_MANAGER': {
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/csm/emr-reports/prescription/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/csm/emr-reports/prescription/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/csm/emr-reports/file/upload',
    },
  },
  // Clinic Success Admin uses same prescription path as Manager.
  'ROLE_CLINIC_SUCCESS_ADMIN': {
    [REPORT_TYPES.PRESCRIPTION]: {
      [API_OPERATIONS.GET]: '/api/secure/cc/support/csm/emr-reports/prescription/images/get-by-eventId',
      [API_OPERATIONS.SAVE]: '/api/secure/cc/support/csm/emr-reports/prescription/images/save',
      [API_OPERATIONS.UPLOAD]: '/api/secure/cc/support/csm/emr-reports/file/upload',
    },
  },
}

/**
 * Detects the current portal based on the window pathname
 * @returns {string|null} Portal type or null if cannot be determined
 */
export function detectCurrentPortal() {
  if (typeof window === 'undefined') return null

  const pathname = window.location.pathname

  if (pathname.startsWith('/support-home') || pathname.startsWith('/support/')) {
    return PORTAL_TYPES.SUPPORT
  }
  if (pathname.startsWith('/mentor-home') || pathname.startsWith('/mentor/')) {
    return PORTAL_TYPES.MENTOR
  }
  if (pathname.startsWith('/ca-home') || pathname.startsWith('/kiosk') || pathname.startsWith('/ca/')) {
    return PORTAL_TYPES.CLINIC_ASSISTANT
  }
  if (pathname.startsWith('/clinic-home') || pathname.startsWith('/clinic/')) {
    return PORTAL_TYPES.CLINIC
  }
  if (pathname.startsWith('/workplace') || pathname.startsWith('/business/')) {
    return PORTAL_TYPES.BUSINESS
  }
  // Consumer portal is the default (root path)
  if (pathname === '/' || (!pathname.startsWith('/support') && 
      !pathname.startsWith('/mentor') && 
      !pathname.startsWith('/ca') && 
      !pathname.startsWith('/clinic') && 
      !pathname.startsWith('/workplace'))) {
    return PORTAL_TYPES.CONSUMER
  }

  return null
}

/**
 * Gets the API endpoint for a specific report type and operation
 * @param {string} reportType - One of REPORT_TYPES values
 * @param {string} operation - One of API_OPERATIONS values
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role for role-based APIs
 * @returns {string|null} API endpoint or null if not available
 */
export function getReportApiEndpoint(reportType, operation, options = {}) {
  const { portalType, supportRole } = options

  // Determine portal type
  let currentPortal = portalType || detectCurrentPortal()
  
  if (!currentPortal) {
    console.warn('Could not determine portal type. Using default.')
    return null
  }

  // Check if portal can access control center APIs
  const cannotAccessCC = [
    PORTAL_TYPES.MENTOR,
    PORTAL_TYPES.CLINIC_ASSISTANT,
    PORTAL_TYPES.CONSUMER,
  ].includes(currentPortal)

  // Get base API config for the portal
  let apiEndpoint = null

  if (currentPortal === PORTAL_TYPES.SUPPORT && supportRole) {
    // Check for role-specific overrides
    const roleOverride = SUPPORT_ROLE_API_OVERRIDES[supportRole]?.[reportType]?.[operation]
    if (roleOverride) {
      apiEndpoint = roleOverride
    }
  }

  // Fall back to default portal API
  if (!apiEndpoint) {
    apiEndpoint = API_CONFIG[currentPortal]?.[reportType]?.[operation] || null
  }

  // Validate that non-CC portals are not using CC APIs
  if (apiEndpoint && cannotAccessCC && apiEndpoint.startsWith('/api/secure/cc/')) {
    console.error(
      `Portal ${currentPortal} cannot access Control Center APIs. ` +
      `Attempted to use: ${apiEndpoint}`
    )
    return null
  }

  return apiEndpoint
}

/**
 * Gets all API endpoints for a report type (get, save, upload)
 * @param {string} reportType - One of REPORT_TYPES values
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getReportApiEndpoints(reportType, options = {}) {
  return {
    get: getReportApiEndpoint(reportType, API_OPERATIONS.GET, options),
    save: getReportApiEndpoint(reportType, API_OPERATIONS.SAVE, options),
    upload: getReportApiEndpoint(reportType, API_OPERATIONS.UPLOAD, options),
  }
}

/**
 * Gets the support role from user's roles
 * Priority: Clinic Success Admin/Manager first (they use different API path),
 * then therapy support roles, then other support roles
 * @returns {string|null} Support role or null
 */
export function getSupportRole() {
  try {
    const roles = getUserRoles()
    const allRoles = getAllRolesInBoolean()

    // Priority order for support roles
    // Clinic Success roles first (they use different API path)
    if (allRoles.isClinicSuccessAdmin) return 'ROLE_CLINIC_SUCCESS_ADMIN'
    if (allRoles.isClinicSuccessManager) return 'ROLE_CLINIC_SUCCESS_MANAGER'
    // Then therapy support roles
    if (allRoles.isL3TherapySupport) return 'ROLE_THERAPY_SUP_L3'
    if (allRoles.isL2TherapySupport) return 'ROLE_THERAPY_SUP_L2'
    if (allRoles.isL1TherapySupport) return 'ROLE_THERAPY_SUP_L1'
    // Then other support roles
    if (allRoles.isClinicRosterManager) return 'ROLE_CLINIC_ROSTER_MANAGER'
    if (roles?.includes('ROLE_THERAPIST_SUPERVISOR')) return 'ROLE_THERAPIST_SUPERVISOR'
    if (roles?.includes('ROLE_THERAPY_SUPERVISOR')) return 'ROLE_THERAPY_SUPERVISOR'
    if (roles?.includes('ROLE_SCREENER_SUPERVISOR')) return 'ROLE_SCREENER_SUPERVISOR'
    if (roles?.includes('ROLE_SUPPORT')) return 'ROLE_SUPPORT'
    if (roles?.includes('ROLE_SUPPORT_USER')) return 'ROLE_SUPPORT_USER'
    if (roles?.includes('ROLE_PAYMENT_SUPPORT')) return 'ROLE_PAYMENT_SUPPORT'

    return null
  } catch (error) {
    console.error('Error getting support role:', error)
    return null
  }
}

/**
 * Convenience function to get API endpoints with automatic portal and role detection
 * @param {string} reportType - One of REPORT_TYPES values
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getReportApis(reportType, options = {}) {
  const portalType = options.portalType || detectCurrentPortal()
  const supportRole = options.supportRole || (portalType === PORTAL_TYPES.SUPPORT ? getSupportRole() : null)

  return getReportApiEndpoints(reportType, {
    ...options,
    portalType,
    supportRole,
  })
}

// Export default configuration for direct access if needed
export default API_CONFIG
